import React from 'react';
import { Container } from '../components/common/Container';
import { Card } from '../components/common/Card';
import { 
  CalculatorIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  FolderOpenIcon,
  CodeBracketIcon,
  UsersIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';

const services = [
  {
    Icon: CalculatorIcon,
    title: 'Cost Approach Valuation',
    description: 'Industry-standard cost approach methodology with automated calculations, current cost lookups, and depreciation schedules. Generate comprehensive cost-based valuations in minutes.'
  },
  {
    Icon: ChartBarIcon,
    title: 'Income Approach Analysis',
    description: 'Powerful income-based valuation tools with cap rate analysis, NOI calculations, and market comparisons. Perfect for commercial properties and income-producing assets.'
  },
  {
    Icon: PresentationChartLineIcon,
    title: 'Equity Approach',
    description: 'Automated comparable property analysis using assessor data. Find and bracket similar properties to build compelling equity arguments.'
  },
  {
    Icon: ClipboardDocumentListIcon,
    title: 'Comprehensive Workups',
    description: 'Generate complete valuation packages combining all three approaches with supporting documentation, photos, and market data—ready for court presentation.'
  },
  {
    Icon: ArrowPathIcon,
    title: 'Appeal Workflow Management',
    description: 'Track every appeal from notice to litigation with customizable stages, automated reminders, and full data persistence. Never lose track of a deadline or status update.'
  },
  {
    Icon: CodeBracketIcon,
    title: 'API Integration',
    description: 'Enterprise-grade REST API for seamless integration with your existing systems. Programmatic access to property data, appeal tracking, and reporting capabilities.'
  },
  {
    Icon: FolderOpenIcon,
    title: 'Document Management',
    description: 'Centralized document storage with version control. Upload and organize rent rolls, income statements, photos, appraisals, and all supporting documentation.'
  },
  {
    Icon: UsersIcon,
    title: 'Team Collaboration',
    description: 'Role-based access control, team assignments, and client portals. Collaborate efficiently across your organization with granular permission controls.'
  }
];

export const Services: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-brand-blue py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Comprehensive Features for Property Tax Professionals
            </h1>
            <p className="text-xl text-brand-blue-100">
              Everything you need to analyze properties, generate reports, manage
              workflows, and win appeals—all in one powerful platform.
            </p>
          </div>
        </Container>
      </section>
      
      {/* Services Grid */}
      <section className="py-24 bg-white">
        <Container>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} hover>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-brand-red rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <service.Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-blue mb-3">
                      {service.title}
                    </h3>
                    <p className="text-brand-white-700">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-brand-blue-50">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-blue mb-6">
              Ready to See These Features in Action?
            </h2>
            <p className="text-lg text-brand-white-700 mb-8">
              Schedule a personalized demo to see how Appeal Advantage can
              transform your property tax appeal workflow.
            </p>
            <a
              href="https://calendly.com/appealadvantage/appeal-advantage-platform-demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-brand-red hover:bg-brand-red-600 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Request a Demo
            </a>
          </div>
        </Container>
      </section>
    </>
  );
};

